import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

function SearchBarNav() {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const searchRef = useRef(null);
    const abortRef = useRef(null);
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        setActiveIndex(0);
    }, [suggestions]);

    useEffect(() => {
        const debounceTimeout = setTimeout(() => {
            const term = query.trim();
            if (!term) {
                setSuggestions([]);
                setShowSuggestions(false);
                return;
            }

            if (abortRef.current) {
                abortRef.current.abort();
            }

            const controller = new AbortController();
            abortRef.current = controller;

            fetch(
                `${apiUrl}/api/search/suggest?query=${encodeURIComponent(term)}&limit=5&tcg=pokemon&types=card,set,series`,
                { signal: controller.signal }
            )
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.json();
                })
                .then((data) => {
                    const results = data.results || [];
                    setSuggestions(results);
                    setShowSuggestions(results.length > 0);
                })
                .catch((error) => {
                    if (error.name === 'AbortError') return;
                    console.error('Error loading suggestions:', error);
                    setSuggestions([]);
                    setShowSuggestions(false);
                });
        }, 150);

        return () => clearTimeout(debounceTimeout);
    }, [query, apiUrl]);

    const handleInputChange = (e) => {
        setQuery(e.target.value);
    };

    const navigateToSearch = (term) => {
        navigate(`/search/${encodeURIComponent(term)}`);
    };

    const handleSuggestionClick = (item) => {
        if (!item) return;
        if (item.is_query && item.label) {
            navigateToSearch(item.label);
            return;
        }
        if (item.type === 'card' && item.id) {
            navigate(`/card/${encodeURIComponent(item.id)}`);
            return;
        }
        if (item.type === 'set' && item.id) {
            navigate(`/sets/${encodeURIComponent(item.id)}`);
            return;
        }
        if (item.type === 'series' && item.id) {
            navigate(`/series/${encodeURIComponent(item.id)}`);
            return;
        }
        if (item.label) {
            navigateToSearch(item.label);
            return;
        }
        if (query.trim()) {
            navigateToSearch(query);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && query.trim()) {
            e.preventDefault();
            navigateToSearch(query);
            return;
        }

        if (!showSuggestions || suggestions.length === 0) {
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((prevIndex) =>
                prevIndex === suggestions.length - 1 ? 0 : prevIndex + 1
            );
        }

        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((prevIndex) =>
                prevIndex === 0 ? suggestions.length - 1 : prevIndex - 1
            );
        }

        if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    return (
        <div className="relative w-full md:w-64" ref={searchRef}>
            <input
                id="search-input"
                type="text"
                placeholder="Search for cards, sets, tcg..."
                className="w-full h-9 rounded-md border border-purple/30 bg-dark/80 px-3 py-1 pl-9 text-sm text-cream placeholder:text-cream/50 focus:outline-none focus:ring-1 focus:ring-purple/50"
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => query.trim() && suggestions.length > 0 && setShowSuggestions(true)}
            />
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-2.5 h-4 w-4 text-purple">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.3-4.3"></path>
            </svg>

            {showSuggestions && (
                <div className="absolute left-0 right-0 mt-1 bg-dark/95 border border-purple/30 rounded-md shadow-lg max-h-[60vh] md:max-h-60 overflow-y-auto z-50">
                    {suggestions.map((item, index) => (
                        <div
                            key={`${item.type || 'query'}-${item.id || item.label}-${index}`}
                            className={`px-3 py-2 cursor-pointer flex justify-between items-center ${index === activeIndex ? 'bg-purple/20' : 'hover:bg-purple/10'}`}
                            onClick={() => handleSuggestionClick(item)}
                            onMouseEnter={() => setActiveIndex(index)}
                        >
                            <span className="text-cream text-sm">{item.label}</span>
                            {item.type && !item.is_query && (
                                <span className="text-xs px-2 py-1 rounded bg-purple/30 text-purple">{item.type}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default SearchBarNav;
